// Lightweight Web-Audio notification chimes (no asset files). Distinct tone per role,
// with independent mute toggles for Services vs Care and a shared volume control.
let ctx = null;
let _vol = null; // 0..1, lazily read

function getCtx() {
    if (!ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctx = new AC();
    }
    return ctx;
}

// Browsers require a user gesture before audio can play — unlock on first interaction.
export function initSoundUnlock() {
    const unlock = () => {
        const c = getCtx();
        if (c && c.state === "suspended") c.resume();
        window.removeEventListener("pointerdown", unlock);
        window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
}

// --- settings (per-mode mute + shared volume) -------------------------------
const MUTE_KEY = { services: "homiie_mute_services", care: "homiie_mute_care" };

// one-time migration from the old single on/off flag
function migrate() {
    if (localStorage.getItem("homiie_sound") === "off" &&
        localStorage.getItem(MUTE_KEY.services) == null) {
        localStorage.setItem(MUTE_KEY.services, "1");
        localStorage.setItem(MUTE_KEY.care, "1");
    }
}
migrate();

export function isMuted(kind = "services") {
    return localStorage.getItem(MUTE_KEY[kind] || MUTE_KEY.services) === "1";
}
export function setMuted(kind, muted) {
    localStorage.setItem(MUTE_KEY[kind] || MUTE_KEY.services, muted ? "1" : "0");
}
export function getVolume() {
    const v = parseInt(localStorage.getItem("homiie_volume") ?? "80", 10);
    return Math.max(0, Math.min(100, isNaN(v) ? 80 : v)) / 100;
}
export function setVolume(pct) {
    localStorage.setItem("homiie_volume", String(Math.max(0, Math.min(100, Math.round(pct)))));
}

function tone(c, freq, start, dur, type = "sine", gain = 0.15) {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    osc.connect(g);
    g.connect(c.destination);
    const t = c.currentTime + start;
    const peak = Math.max(0.0001, gain * (_vol == null ? getVolume() : _vol));
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.start(t);
    osc.stop(t + dur + 0.02);
}

// Distinct patterns so users recognise who the alert is for.
const PATTERNS = {
    customer: (c) => { tone(c, 660, 0, 0.18); tone(c, 880, 0.16, 0.22); },
    care: (c) => { tone(c, 587, 0, 0.16); tone(c, 740, 0.15, 0.16); tone(c, 880, 0.3, 0.26); },
    parent: (c) => { tone(c, 523, 0, 0.22, "sine", 0.18); tone(c, 659, 0.22, 0.3, "sine", 0.18); },
    guardian: (c) => { tone(c, 587, 0, 0.16); tone(c, 784, 0.15, 0.24); },
    professional: (c) => { tone(c, 988, 0, 0.12, "square", 0.12); tone(c, 988, 0.16, 0.12, "square", 0.12); tone(c, 1319, 0.32, 0.2, "square", 0.12); },
    admin: (c) => { tone(c, 440, 0, 0.14, "sawtooth", 0.14); tone(c, 440, 0.18, 0.14, "sawtooth", 0.14); tone(c, 440, 0.36, 0.14, "sawtooth", 0.14); tone(c, 660, 0.54, 0.25, "sawtooth", 0.14); },
};

// kind decides which mute toggle gates the sound: "care" or "services".
export function playChime(role = "customer", emergency = false, kind = null) {
    const k = kind || (role === "care" ? "care" : "services");
    if (isMuted(k) && !emergency) return; // emergencies always ring unless the whole mode is muted
    if (isMuted(k)) return;
    const c = getCtx();
    if (!c) return;
    if (c.state === "suspended") c.resume();
    _vol = getVolume();
    const key = emergency ? "admin" : (PATTERNS[role] ? role : "customer");
    try { PATTERNS[key](c); } catch (e) { /* no-op */ }
}

// Play a short preview for the settings UI (ignores mute, uses current volume).
export function previewChime(role = "customer") {
    const c = getCtx();
    if (!c) return;
    if (c.state === "suspended") c.resume();
    _vol = getVolume();
    try { (PATTERNS[role] || PATTERNS.customer)(c); } catch (e) { /* no-op */ }
}


// Loud two-tone siren for emergency escalations — ignores mute (life-safety).
export function playSiren() {
    const c = getCtx();
    if (!c) return;
    if (c.state === "suspended") c.resume();
    _vol = Math.max(0.6, getVolume());
    try { for (let i = 0; i < 4; i++) { tone(c, 960, i * 0.5, 0.24, "sawtooth", 0.22); tone(c, 640, i * 0.5 + 0.25, 0.24, "sawtooth", 0.22); } } catch (e) { /* no-op */ }
}
